import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { HttpClient } from '@angular/common/http';
import { UserEmailConflictDTO } from '../../../../dto/users/user-email-conflict.dto';
import { environment } from '../../../../environment/environment';
import { DynamicTableComponent } from '../../../dynamic-table/dynamic-table.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-email-conflict-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatListModule,
    MatChipsModule,
    MatTooltipModule,
    MatTableModule,
    MatSnackBarModule,
    DynamicTableComponent,
  ],
  template: `
    <div class="conflict-dialog">
      <h2 mat-dialog-title>
        <mat-icon>warning</mat-icon>
        Email Konflikter
      </h2>

      <mat-dialog-content>
        <div class="info-banner">
          <mat-icon>info</mat-icon>
          <div>
            <strong>Email Konflikter:</strong> Disse konflikter opstår når sync
            processen finder emails der måske tilhører eksisterende brugere.
            Gennemgå hver konflikt og vælg om emailen skal tilføjes til en
            eksisterende bruger eller skal kasseres.
          </div>
        </div>

        <div class="conflicts-table">
          <app-dynamic-table
            #conflictsTable
            [dynamicInput]="{
              dataSource: { type: 'text', value: 'email_conflicts' }
            }"
          >
          </app-dynamic-table>
        </div>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-button (click)="close()">Luk</button>
      </mat-dialog-actions>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .conflict-dialog {
        min-width: 1200px;
        max-width: 1600px;
        width: 90vw;
      }

      h2[mat-dialog-title] {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 20px;
      }

      mat-dialog-content {
        max-height: 70vh;
        overflow-y: auto;
      }

      .info-banner {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 16px;
        background-color: #e3f2fd;
        border-left: 4px solid #2196f3;
        border-radius: 4px;
        margin-bottom: 20px;
      }

      .info-banner mat-icon {
        color: #2196f3;
        margin-top: 2px;
      }

      .conflicts-table {
        margin-top: 20px;
      }
    `,
  ],
})
export class EmailConflictDialogComponent implements OnInit, OnDestroy {
  conflicts: UserEmailConflictDTO[] = [];
  @ViewChild('conflictsTable') conflictsTable?: DynamicTableComponent;
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<EmailConflictDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Listen for resolve conflict events from dynamic-table
    window.addEventListener(
      'resolve-email-conflict',
      this.handleResolveConflict.bind(this) as EventListener
    );
  }

  ngOnDestroy(): void {
    window.removeEventListener(
      'resolve-email-conflict',
      this.handleResolveConflict.bind(this) as EventListener
    );
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleResolveConflict(event: CustomEvent): void {
    const conflict: UserEmailConflictDTO = event.detail;
    this.resolveConflict(conflict);
  }

  resolveConflict(conflict: UserEmailConflictDTO): void {
    if (!conflict.users_id) {
      this.snackBar.open(
        'Fejl: Konflikten har ikke en tilknyttet bruger',
        'Luk',
        { duration: 5000 }
      );
      return;
    }

    const warningMessage = `
      <div style="max-width: 500px;">
        <h3 style="margin-top: 0; color: #d32f2f;">ADVARSEL: Email Tilføjelse</h3>
        <p><strong>Du er ved at tilføje følgende email til en bruger:</strong></p>
        <ul>
          <li><strong>Email:</strong> ${conflict.proposed_email}</li>
          <li><strong>Eksisterende email:</strong> ${
            conflict.existing_email || 'Ingen'
          }</li>
          <li><strong>Mobilnummer:</strong> ${conflict.mobile || 'Mangler'}</li>
        </ul>
        <p style="color: #d32f2f; font-weight: bold;">
          ⚠️ VIGTIGT: Sikre dig at dette er den samme person før du fortsætter!
        </p>
        <p>Denne handling kan ikke fortrydes uden administrativ indgriben.</p>
      </div>
    `;

    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      width: '600px',
      data: {
        title: 'Bekræft Email Tilføjelse',
        message: warningMessage,
        confirmText: 'Ja, tilføj email',
        cancelText: 'Annuller',
        confirmColor: 'warn',
      },
    });

    confirmDialog.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.performResolveConflict(conflict);
      }
    });
  }

  private performResolveConflict(conflict: UserEmailConflictDTO): void {
    const url = `${environment.apiUrl}/users/manage/conflicts/${conflict.id}/resolve`;
    const body = {
      user_id: conflict.users_id,
      action: 'add_email',
    };

    this.http.post(url, body).subscribe({
      next: (response: any) => {
        this.snackBar.open('Email blev tilføjet succesfuldt', 'Luk', {
          duration: 3000,
        });
        // Close dialog and let parent refresh
        setTimeout(() => {
          this.dialogRef.close({ refresh: true });
        }, 1000);
      },
      error: (error: any) => {
        console.error('Error resolving conflict:', error);
        let errorMessage = 'Fejl ved tilføjelse af email';
        if (error.error && typeof error.error === 'string') {
          errorMessage = error.error;
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.statusText) {
          errorMessage = `${error.statusText} (${error.status || 'Unknown'})`;
        }
        this.snackBar.open(errorMessage, 'Luk', { duration: 7000 });
      },
    });
  }

  close(): void {
    this.dialogRef.close({ refresh: true });
  }
}

// Simple confirmation dialog component
@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <mat-dialog-content [innerHTML]="data.message"></mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close(false)">
        {{ data.cancelText || 'Annuller' }}
      </button>
      @if (data.confirmColor !== 'warn') {
      <button
        mat-button
        [color]="data.confirmColor || 'primary'"
        (click)="dialogRef.close(true)"
      >
        {{ data.confirmText || 'Bekræft' }}
      </button>
      } @if (data.confirmColor === 'warn') {
      <button mat-raised-button color="warn" (click)="dialogRef.close(true)">
        {{ data.confirmText || 'Bekræft' }}
      </button>
      }
    </mat-dialog-actions>
  `,
})
class ConfirmDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}
}
